import { useQuery } from "@tanstack/react-query";
import { ChangeEvent, useEffect, useState } from "react";
import { request } from "../api";
import {
  GET_LOW_MACHINERY_SELECT_OPTIONS,
  GET_MACHINERY_SELECT_OPTIONS,
  GET_REPORT_TYPES,
  GET_SENSOR_SELECT_OPTIONS,
  GET_SHIP_SELECT_OPTIONS,
  GET_SHIP_TYPE_SELECT_OPTIONS,
} from "../query-keys";
import { SelectOption } from "../types/selectOption";
import { selectManagerProps } from "../types/report";

const useSelectManager = (searchDefaultValues: selectManagerProps) => {
  const [search, setSearch] = useState<selectManagerProps>(searchDefaultValues);

  // 보고서 유형 options 조회
  const { data: reportSelectOptions } = useQuery<SelectOption[]>({
    queryKey: [GET_REPORT_TYPES],
    queryFn: async () => {
      const result = await request<SelectOption[]>({
        method: "GET",
        url: "/select/options/report",
      });

      return result;
    },
  });

  // 함형 options 조회
  const { data: shipTypeSelectOptions } = useQuery<SelectOption[]>({
    queryKey: [GET_SHIP_TYPE_SELECT_OPTIONS, "all"],
    queryFn: async () => {
      const result = await request<SelectOption[]>({
        method: "GET",
        url: "/equipment-code/select/options",
      });

      return result;
    },
  });

  // 함정 options 조회
  const { data: shipSelectOptions } = useQuery<SelectOption[]>({
    queryKey: [GET_SHIP_SELECT_OPTIONS, search.shipTypeNo],
    queryFn: async () => {
      if (!search.shipTypeNo) {
        return [];
      }

      return await request<SelectOption[]>({
        method: "GET",
        url: `/ship-info/select/options/${search.shipTypeNo}`,
      });
    },
  });

  // 장비 options 조회
  const { data: machinerySelectOptions } = useQuery<SelectOption[]>({
    queryKey: [GET_MACHINERY_SELECT_OPTIONS, search.shipNo],
    queryFn: async () => {
      if (!search.shipNo) {
        return [];
      }

      return await request<SelectOption[]>({
        method: "GET",
        url: `/equipment-info/machinery/select/options/${search.shipNo}`,
      });
    },
  });

  // 계통 options 조회
  const { data: lowMachinerySelectOptions, refetch: lowMachineRefetch } =
    useQuery<SelectOption[]>({
      queryKey: [GET_LOW_MACHINERY_SELECT_OPTIONS, search.mcyName],
      queryFn: async () => {
        if (!search.mcyName) {
          return [];
        }
        return await request<SelectOption[]>({
          method: "GET",
          url: `/equipment-info/low-machinery/select/options/${search.mcyName}`,
        });
      },
    });

  // 센서 options 조회
  const { data: sensorSelectOptions } = useQuery<SelectOption[]>({
    queryKey: [GET_SENSOR_SELECT_OPTIONS, search.lowMcyName],
    queryFn: async () => {
      if (!search.lowMcyName) {
        return [];
      }

      return await request<SelectOption[]>({
        method: "GET",
        url: `/sensor/select/options`,
        params: {
          ssrShipNo: search.shipNo,
          ssrMcyName: search.mcyName,
          ssrLowMcyName: search.lowMcyName,
        },
      });
    },
  });

  // 함형 선택 변경
  const handleShipTypeSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSearch({
      ...search,
      shipTypeNo: e.target.value,
      shipTypeName: e.target.options[e.target.selectedIndex].text,
    });
  };

  // 함정 선택 변경
  const handleShipSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSearch({
      ...search,
      shipNo: e.target.value,
      shipName: e.target.options[e.target.selectedIndex].text,
    });
  };

  // 장비 선택 변경
  const handleMcySelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSearch({
      ...search,
      mcyName: e.target.options[e.target.selectedIndex].text,
      mcyNo: e.target.value,
    });
  };

  // 계통 선택 변경
  const handleLowMcySelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSearch({
      ...search,
      lowMcyName: e.target.value,
    });
  };

  // 센서 값 변경
  const handleSensorChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setSearch({
      ...search,
      [name]: value,
    });
  };

  // 날짜 변경
  const handleDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSearch({
      ...search,
      [name]: value,
    });
  };

  // 상태변경 자유 함수
  const setSearchData = (key: string, value: any) => {
    setSearch((prevSearch) => ({
      ...prevSearch,
      [key]: value,
    }));
  };

  // searchDefaultValues 변경 시 마다 search 초기화
  useEffect(() => {
    setSearch(searchDefaultValues);
    lowMachineRefetch();
  }, [searchDefaultValues.mcyName]);

  return {
    search,

    /** 보고서 유형 select options */
    reportSelectOptions,

    /** 함형 select options */
    shipTypeSelectOptions,

    /** 함정 select options */
    shipSelectOptions,

    /** 장비 select options */
    machinerySelectOptions,

    /** 하위장비 select options */
    lowMachinerySelectOptions,

    /** 센서 select options */
    sensorSelectOptions,

    /** 함형 선택 변경 */
    handleShipTypeSelectChange,

    /** 함정 선택 변경 */
    handleShipSelectChange,

    /** 장비 선택 변경 */
    handleMcySelectChange,

    /** 하위장비 선택 변경 */
    handleLowMcySelectChange,

    /** 센서 값 변경 */
    handleSensorChange,

    /** 날짜 변경*/
    handleDateChange,
    /** 상태변경 자유 함수 */
    setSearchData,
    setSearch,
  };
};

export default useSelectManager;
